// @ts-nocheck
import { useState, useEffect, useMemo, useCallback, useRef } from "react";

export type {
  MetricValue,
  AccountMetrics,
  CategoryScore,
  AccountMatrixData,
  MetricsConfiguration,
  HawkeyeMetricsAccountsInput,
  AccountInfo,
  HawkeyeChartMetricsProps,
} from "./hawkeyeMetricsCore";

export {
  getDefaultMetricsConfiguration,
  loadMetricsConfigurationFromApi,
  fetchHawkeyeDigestState,
  buildAccountMatrixData,
  getAvailableCategories,
  getOrderedCategories,
  normalizeAccounts,
  resolveDigestDateRange,
  emptyDigestState,
} from "./hawkeyeMetricsCore";

import type {
  AccountMatrixData,
  HawkeyeMetricsAccountsInput,
  MetricsConfiguration,
  HawkeyeDigestState,
} from "./hawkeyeMetricsCore";
import {
  loadMetricsConfigurationFromApi,
  fetchHawkeyeDigestState,
  buildAccountMatrixData,
  getAvailableCategories,
  getOrderedCategories,
  normalizeAccounts,
  resolveDigestDateRange,
  emptyDigestState,
  getDefaultMetricsConfiguration,
} from "./hawkeyeMetricsCore";
import { useOrgSyntheticDataToggle } from "../../Hooks/useOrgSyntheticDataToggle";

export const useHawkeyeMetrics = (
  accountsInput: HawkeyeMetricsAccountsInput | undefined,
  _selectedKPIs?: string[],
  measurementDate?: string | null,
  options?: { requireDate?: boolean }
) => {
  const [configuration, setConfiguration] = useState<MetricsConfiguration>(
    () => getDefaultMetricsConfiguration()
  );
  const [configurationLoading, setConfigurationLoading] = useState(false);
  const [digestState, setDigestState] = useState<HawkeyeDigestState>(() =>
    emptyDigestState()
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const lastFetchKeyRef = useRef<string>("");

  const isLoading = loading || configurationLoading;
  const requireDate = options?.requireDate ?? true;
  const { dataModeKey: refreshKey } = useOrgSyntheticDataToggle();

  // Force a new fetch whenever mode (or other refreshKey) changes.
  useEffect(() => {
    lastFetchKeyRef.current = "";
  }, [refreshKey]);

  const { digestFromDate, digestToDate } = useMemo(
    () => resolveDigestDateRange(accountsInput, measurementDate),
    [
      accountsInput?.digestFromDate,
      accountsInput?.digestToDate,
      accountsInput?.selectedMeasurementDate,
      accountsInput?.latestMeasurementDate,
      measurementDate,
    ]
  );

  const { rowAccounts, fetchAccounts } = useMemo(
    () => normalizeAccounts(accountsInput),
    [accountsInput]
  );

  const loadConfiguration = useCallback(async () => {
    setConfigurationLoading(true);
    try {
      const next = await loadMetricsConfigurationFromApi();
      setConfiguration(next);
    } finally {
      setConfigurationLoading(false);
    }
  }, []);

  const matrixData = useMemo((): AccountMatrixData[] => {
    return buildAccountMatrixData(rowAccounts, configuration, digestState);
  }, [rowAccounts, configuration, digestState]);

  const fetchMetrics = useCallback(async () => {
    if (!accountsInput || !fetchAccounts.length) {
      setDigestState(emptyDigestState());
      setLoading(false);
      return;
    }

    const accountIds = fetchAccounts
      .map(a => String(a.id || "").trim())
      .filter(Boolean);

    if (!accountIds.length) {
      setDigestState(emptyDigestState());
      setLoading(false);
      return;
    }

    const sortedAccountIds = [...accountIds].sort();
    const requestKey = requireDate
      ? `${sortedAccountIds.join(",")}|${digestFromDate || "none"}|${digestToDate || "none"}|${refreshKey}`
      : `${sortedAccountIds.join(",")}|all-dates|${refreshKey}`;

    if (
      lastFetchKeyRef.current === requestKey &&
      !error &&
      digestState.digestAccountIds.size > 0
    ) {
      return;
    }
    lastFetchKeyRef.current = requestKey;

    setLoading(true);
    setError(null);

    try {
      const nextDigest = await fetchHawkeyeDigestState(
        accountIds,
        digestFromDate,
        digestToDate,
        requireDate,
        refreshKey
      );
      setDigestState(nextDigest);
    } catch (digestErr) {
      console.warn("Failed to load Hawkeye digests:", digestErr);
      setDigestState(emptyDigestState());
    } finally {
      setLoading(false);
    }
  }, [
    accountsInput,
    fetchAccounts,
    digestFromDate,
    digestToDate,
    requireDate,
    refreshKey,
    digestState.digestAccountIds.size,
    error,
  ]);

  useEffect(() => {
    loadConfiguration();
  }, [loadConfiguration]);

  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  const updateConfiguration = useCallback(
    (newConfig: Partial<MetricsConfiguration>) => {
      setConfiguration(prev => ({ ...prev, ...newConfig }));
    },
    []
  );

  const availableCategories = useMemo(() => {
    return getAvailableCategories(digestState.digestAiScores);
  }, [digestState.digestAiScores]);

  const orderedCategories = useMemo(() => {
    return getOrderedCategories(configuration, availableCategories);
  }, [availableCategories, configuration]);

  const reloadConfiguration = useCallback(async () => {
    await loadConfiguration();
  }, [loadConfiguration]);

  return {
    matrixData,
    loading: isLoading,
    error,
    configuration,
    updateConfiguration,
    availableCategories,
    orderedCategories,
    refetch: fetchMetrics,
    reloadConfiguration,
  };
};
